<div data-spartan-page style="background: rgb(var(--ds-bg, 250 250 249)); min-height: 100%;"
  (click)="openMoreMenuId = null">
  <div class="mx-auto max-w-screen-xl px-4 py-6 space-y-6">

    <!-- Page Header -->
    <app-page-header eyebrow="Admin" title="Client Integration Management"
      subtitle="Manage third-party client integrations and user access">
      <ng-container actions>
        @if (!showCreateClient && !showEditClient) {
          <button type="button" hlmBtn size="sm" (click)="showCreateClientForm()">
            <ng-icon hlm name="lucidePlusCircle" class="size-4 shrink-0" />Add Client
          </button>
        }
      </ng-container>
    </app-page-header>

    <!-- Alerts -->
    @if (successMessage) {
      <div class="flex items-start gap-3 rounded-lg border border-green-200 dark:border-green-500/30 bg-green-50 dark:bg-green-500/15 px-4 py-3 text-sm text-green-800 dark:text-green-300">
        <ng-icon hlm name="lucideCheckCircle" class="size-4 mt-0.5 shrink-0" />
        <span class="flex-1">{{ successMessage }}</span>
        <button type="button" class="shrink-0 opacity-60 hover:opacity-100 appearance-none border-0 bg-transparent focus-visible:ring-2 focus-visible:ring-accent rounded" (click)="clearMessages()" aria-label="Dismiss">
          <ng-icon hlm name="lucideX" class="size-4" />
        </button>
      </div>
    }
    @if (errorMessage) {
      <div class="flex items-start gap-3 rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 px-4 py-3 text-sm text-red-800 dark:text-red-300">
        <ng-icon hlm name="lucideAlertTriangle" class="size-4 mt-0.5 shrink-0" />
        <span class="flex-1">{{ errorMessage }}</span>
        <button type="button" class="shrink-0 opacity-60 hover:opacity-100 appearance-none border-0 bg-transparent focus-visible:ring-2 focus-visible:ring-accent rounded" (click)="clearMessages()" aria-label="Dismiss">
          <ng-icon hlm name="lucideX" class="size-4" />
        </button>
      </div>
    }

    <!-- Loading -->
    @if (isLoading && !showCreateClient && !showEditClient) {
      <div class="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
        <div class="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-ds-accent"></div>
        <span class="text-sm">Loading client integrations...</span>
      </div>
    }

    <!-- ── Create / Edit Panel ── -->
    @if (showCreateClient || showEditClient) {
      <div hlmCard class="overflow-hidden">

        <!-- Edit Mode: Tabs + content -->
        @if (showEditClient && selectedClient) {

          <!-- Tab Bar -->
          <div class="border-b border-border bg-ds-surface-raised/60">
            <div class="flex overflow-x-auto">
              @for (tab of [
                {id:'overview',    label:'Overview',   icon:'lucideInfo'},
                {id:'credentials', label:'Credentials',icon:'lucideKey'},
                {id:'endpoints',   label:'Endpoints',  icon:'lucideLink'},
                {id:'users',       label:'Users',      icon:'lucideUsers'},
                {id:'testing',     label:'Testing',    icon:'lucidePlayCircle'},
                {id:'audit',       label:'Audit Logs', icon:'lucideClock'}
              ]; track tab.id) {
                <button type="button"
                  class="flex items-center gap-1.5 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors"
                  [class]="activeTab === tab.id
                    ? 'border-ds-accent text-ds-accent'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'"
                  (click)="onTabChange(tab.id)">
                  <ng-icon hlm [name]="tab.icon" class="size-3.5" />{{ tab.label }}
                </button>
              }
            </div>
          </div>

          <div class="p-6">
            @switch (activeTab) {

              <!-- OVERVIEW TAB -->
              @case ('overview') {
                <form [formGroup]="clientForm" (ngSubmit)="saveClient()" class="space-y-6">

                  <!-- Basic fields -->
                  <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div class="space-y-1.5">
                      <label class="text-sm font-medium">Client Name <span class="text-red-500">*</span></label>
                      <input type="text" formControlName="clientName"
                        class="h-9 w-full rounded-md border px-3 text-sm"
                        [class]="hasFieldError(clientForm,'clientName') ? 'border-red-400 bg-red-50' : 'border-border-strong bg-background'"
                        placeholder="Enter client name">
                      @if (hasFieldError(clientForm,'clientName')) {
                        <p class="text-xs text-red-600">{{ getFieldError(clientForm,'clientName') }}</p>
                      }
                    </div>
                    <div class="space-y-1.5">
                      <label class="text-sm font-medium">Client Type <span class="text-red-500">*</span></label>
                      <select formControlName="clientType"
                        class="h-9 w-full rounded-md border px-3 text-sm"
                        [class]="hasFieldError(clientForm,'clientType') ? 'border-red-400 bg-red-50' : 'border-border-strong bg-background'">
                        @for (opt of providerOptions; track opt.value) {
                          <option [value]="opt.value">{{ opt.label }}</option>
                        }
                      </select>
                    </div>
                  </div>

                  <!-- Credential Status Summary -->
                  <div hlmCard class="border-blue-200 bg-blue-50/40">
                    <div class="p-4 space-y-3">
                      <div class="flex items-center gap-2 text-sm font-semibold text-blue-800">
                        <ng-icon hlm name="lucideShieldCheck" class="size-4 shrink-0" />Integration Credential Status
                      </div>
                      <p class="text-sm text-blue-700">{{ getCredentialSummary() }}</p>
                      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <!-- INBOUND -->
                        <div class="space-y-1 text-sm">
                          <div class="flex items-center gap-2">
                            <ng-icon hlm name="lucideArrowDownCircle" class="size-4 shrink-0 text-green-600" />
                            <strong>INBOUND</strong>
                            <span hlmBadge [class]="getCredentialStatusBadgeClass(getInboundCredentialStatus())" class="text-xs">
                              {{ getInboundCredentialStatus() }}
                            </span>
                          </div>
                          <div class="ml-5 space-y-0.5 text-xs text-muted-foreground">
                            @if (selectedClient.expectedAccountFromClient) {
                              <div class="flex items-center gap-1"><ng-icon hlm name="lucideCheckCircle" class="size-3.5 shrink-0 text-green-500" />Account: <strong>{{ selectedClient.expectedAccountFromClient }}</strong></div>
                            }
                            @if (selectedClient.hasInboundSecret) {
                              <div class="flex items-center gap-1"><ng-icon hlm name="lucideCheckCircle" class="size-3.5 shrink-0 text-green-500" />Secret: <strong>Configured</strong></div>
                            }
                            @if (!hasInboundCredentials()) {
                              <div class="flex items-center gap-1"><ng-icon hlm name="lucideAlertTriangle" class="size-3.5 shrink-0 text-amber-500" />Generate credentials in Credentials tab</div>
                            }
                          </div>
                        </div>
                        <!-- OUTBOUND -->
                        <div class="space-y-1 text-sm">
                          <div class="flex items-center gap-2">
                            <ng-icon hlm name="lucideArrowUpCircle" class="size-4 shrink-0 text-accent" />
                            <strong>OUTBOUND</strong>
                            <span hlmBadge [class]="getCredentialStatusBadgeClass(getOutboundCredentialStatus())" class="text-xs">
                              {{ getOutboundCredentialStatus() }}
                            </span>
                          </div>
                          <div class="ml-5 space-y-0.5 text-xs text-muted-foreground">
                            @if (selectedClient.ourAccountForClient) {
                              <div class="flex items-center gap-1"><ng-icon hlm name="lucideCheckCircle" class="size-3.5 shrink-0 text-green-500" />Account: <strong>{{ selectedClient.ourAccountForClient }}</strong></div>
                            }
                            @if (selectedClient.hasOurSecretForClient) {
                              <div class="flex items-center gap-1"><ng-icon hlm name="lucideCheckCircle" class="size-3.5 shrink-0 text-green-500" />Secret: Configured</div>
                            }
                            @if (!hasOutboundCredentials) {
                              <div class="flex items-center gap-1"><ng-icon hlm name="lucideAlertTriangle" class="size-3.5 shrink-0 text-amber-500" />Configure in Credentials tab</div>
                            }
                          </div>
                        </div>
                      </div>
                      <!-- Meta row -->
                      <div class="flex flex-wrap gap-4 border-t border-blue-200 pt-3 text-xs text-muted-foreground">
                        <span class="flex items-center gap-1">
                          <ng-icon hlm name="lucideGlobe" class="size-3.5 shrink-0" />Environment:
                          <span hlmBadge [class]="getEnvironmentBadgeClass(selectedClient.environment)" class="ml-1 text-[11px]">
                            {{ getEnvironmentDisplay(selectedClient.environment) }}
                          </span>
                        </span>
                        <span class="flex items-center gap-1">
                          <ng-icon hlm name="lucideArrowUpRight" class="size-3.5 shrink-0" />Priority:
                          <span hlmBadge [class]="getPriorityBadgeClass(selectedClient.configurationPriority)" class="ml-1 text-[11px]">
                            {{ getConfigurationPriorityDisplay(selectedClient.configurationPriority) }}
                          </span>
                        </span>
                        <span class="flex items-center gap-1">
                          <ng-icon hlm name="lucideStar" class="size-3.5 shrink-0" />Default:
                          <span hlmBadge [class]="isDefaultClient() ? 'bg-green-50 text-green-700 ring-1 ring-green-200' : 'bg-stone-100 text-stone-600 ring-1 ring-stone-200'" class="ml-1 text-[11px]">
                            {{ isDefaultClient() ? 'Yes' : 'No' }}
                          </span>
                        </span>
                      </div>
                      <div class="flex items-center justify-between pt-1">
                        <div class="flex items-center gap-2 text-xs text-muted-foreground">
                          <ng-icon hlm name="lucidePlayCircle" class="size-3.5 shrink-0" />Last Test:
                          <span hlmBadge [class]="getIntegrationTestBadgeClass()" class="text-[11px]">{{ getIntegrationTestStatus() }}</span>
                          <span>{{ formatLastIntegrationTest() }}</span>
                        </div>
                        <div class="flex gap-2">
                          <button type="button" hlmBtn size="sm" variant="outline" (click)="onTabChange('credentials')">
                            <ng-icon hlm name="lucideSettings" class="size-4 shrink-0" />Manage Credentials
                          </button>
                          <button type="button" hlmBtn size="sm" variant="outline" (click)="onTabChange('testing')">
                            <ng-icon hlm name="lucidePlayCircle" class="size-4 shrink-0" />Run Tests
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Read-only credential fields -->
                  <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div class="space-y-1.5">
                      <label class="text-sm font-medium">API Base URL</label>
                      <div class="flex gap-1.5">
                        <input type="text" class="h-9 flex-1 rounded-md border border-border bg-stone-50 px-3 text-sm text-muted-foreground"
                          [value]="selectedClient.apiBaseUrl || 'Not configured'" readonly>
                        <button type="button" hlmBtn size="sm" variant="outline" (click)="onTabChange('credentials')">
                          <ng-icon hlm name="lucideSettings" class="size-3.5" />
                        </button>
                      </div>
                    </div>
                    <div class="space-y-1.5">
                      <label class="text-sm font-medium">Client Account ID</label>
                      <div class="flex gap-1.5">
                        <input type="text" class="h-9 flex-1 rounded-md border border-border bg-stone-50 px-3 text-sm text-muted-foreground"
                          [value]="selectedClient.clientAccountId || 'Not configured'" readonly>
                        <button type="button" hlmBtn size="sm" variant="outline" (click)="onTabChange('credentials')">
                          <ng-icon hlm name="lucideSettings" class="size-3.5" />
                        </button>
                      </div>
                    </div>
                    <div class="space-y-1.5">
                      <label class="text-sm font-medium">Exp. Account from Client</label>
                      <input type="text" class="h-9 w-full rounded-md border border-border bg-stone-50 px-3 text-sm text-muted-foreground"
                        [value]="selectedClient.expectedAccountFromClient || 'Not configured'" readonly>
                    </div>
                    <div class="space-y-1.5">
                      <label class="text-sm font-medium">Has Inbound Secret</label>
                      <div class="flex items-center h-9 gap-2">
                        <span hlmBadge [class]="selectedClient.hasInboundSecret ? 'bg-green-50 text-green-700 ring-1 ring-green-200' : 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'">
                          {{ selectedClient.hasInboundSecret ? 'Yes' : 'No' }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Description -->
                  <div class="space-y-1.5">
                    <label class="text-sm font-medium">Description</label>
                    <textarea formControlName="description" rows="3"
                      class="w-full rounded-md border px-3 py-2 text-sm"
                      [class]="hasFieldError(clientForm,'description') ? 'border-red-400 bg-red-50' : 'border-border-strong bg-background'"
                      placeholder="Enter integration description"></textarea>
                    @if (hasFieldError(clientForm,'description')) {
                      <p class="text-xs text-red-600">{{ getFieldError(clientForm,'description') }}</p>
                    }
                  </div>

                  <div class="flex justify-end gap-2 pt-2">
                    <button type="button" hlmBtn variant="outline" (click)="cancelClientForm()" [disabled]="isSaving">Cancel</button>
                    <button type="submit" hlmBtn [disabled]="isSaving">
                      @if (isSaving) { <span class="me-2 h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white"></span> }
                      {{ isSaving ? 'Saving...' : 'Save Changes' }}
                    </button>
                  </div>
                </form>
              }

              <!-- CREDENTIALS TAB -->
              @case ('credentials') {
                <div class="space-y-6">
                  @if (isLoading) {
                    <div class="flex items-center justify-center py-8 text-muted-foreground text-sm gap-2">
                      <div class="h-5 w-5 animate-spin rounded-full border-2 border-border border-t-ds-accent"></div>
                      Loading credentials...
                    </div>
                  }
                  @if (!isLoading) {
                    <div class="flex items-center justify-between">
                      <h3 class="text-sm font-semibold">API Credentials Management</h3>
                      <button type="button" hlmBtn size="sm" variant="outline"
                        (click)="showAuthenticationModalForm(selectedClient)" [disabled]="isSaving">
                        <ng-icon hlm name="lucideShieldCheck" class="size-4 shrink-0" />Authentication Management
                      </button>
                    </div>

                    <!-- INBOUND / OUTBOUND cards -->
                    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <!-- INBOUND -->
                      <div hlmCard class="overflow-hidden" [class.ring-1]="clientCredentials && hasGeneratedCredentials"
                        [class.ring-green-300]="clientCredentials && hasGeneratedCredentials">
                        <div class="flex items-center justify-between border-b border-border px-4 py-3">
                          <div class="flex items-center gap-2 text-sm font-medium">
                            <ng-icon hlm name="lucideArrowDownCircle" class="size-4 shrink-0 text-green-600" />INBOUND Credentials
                          </div>
                          @if (clientCredentials && hasGeneratedCredentials) {
                            <span hlmBadge class="bg-green-50 text-green-700 ring-1 ring-green-200 text-xs">
                              <ng-icon hlm name="lucideWand2" class="size-3.5 shrink-0" />Auto-Generated
                            </span>
                          }
                        </div>
                        <div class="p-4">
                          @if (clientCredentials && hasGeneratedCredentials) {
                            <div class="space-y-2 text-sm">
                              <div>
                                <span class="text-xs text-muted-foreground">Auth Type</span>
                                <div><span hlmBadge class="bg-blue-50 text-blue-700 ring-1 ring-blue-200 text-xs">JWT (Generated)</span></div>
                              </div>
                              <div>
                                <span class="text-xs text-muted-foreground">API Account</span>
                                <div><code class="text-green-700 text-xs">{{ clientCredentials.apiAccount }}</code></div>
                              </div>
                              @if (shouldShowJwtSecretField()) {
                                <div class="space-y-1">
                                  <span class="text-xs text-muted-foreground">Secret Key</span>
                                  <div class="flex gap-1">
                                    <input type="text" class="h-8 flex-1 rounded border border-border bg-stone-50 px-2 font-mono text-xs"
                                      [value]="jwtSecretDisplayValue" readonly
                                      [class.text-green-700]="jwtSecretState.isVisible"
                                      [class.text-muted-foreground]="!jwtSecretState.isVisible">
                                    <button type="button" hlmBtn size="sm" variant="outline" class="h-8 px-2"
                                      (click)="copyJwtSecretToClipboard()" [disabled]="!canCopyJwtSecret"
                                      [title]="canCopyJwtSecret ? 'Copy JWT secret' : 'Regenerate to copy'">
                                      <ng-icon hlm name="lucideClipboard" class="size-3.5" />
                                    </button>
                                  </div>
                                  @if (jwtSecretState.isVisible) {
                                    <p class="text-xs text-green-600 flex items-center gap-1"><ng-icon hlm name="lucideCheckCircle" class="size-3.5 shrink-0" />Visible ({{ jwtSecretState.countdown }}s)</p>
                                  } @else {
                                    <p class="text-xs text-muted-foreground flex items-center gap-1"><ng-icon hlm name="lucideInfo" class="size-3.5 shrink-0" />Regenerate to copy new secret</p>
                                  }
                                </div>
                              }
                              <div>
                                <span class="text-xs text-muted-foreground">Status</span>
                                <div>
                                  <span hlmBadge [class]="clientCredentials.isActive ? 'bg-green-50 text-green-700 ring-1 ring-green-200' : 'bg-red-50 text-red-700 ring-1 ring-red-200'" class="text-xs">
                                    {{ clientCredentials.isActive ? 'Active' : 'Inactive' }}
                                  </span>
                                </div>
                              </div>
                              <div class="text-xs text-muted-foreground">Created: {{ clientCredentials.createdAtUtc | date:'medium' }}</div>
                            </div>
                          } @else {
                            <div class="flex flex-col items-center gap-2 py-6 text-center text-muted-foreground">
                              <ng-icon hlm name="lucideShieldOff" class="size-6" />
                              <p class="text-sm font-medium">No INBOUND Credentials</p>
                              <p class="text-xs">Generate API credentials to enable automatic INBOUND setup</p>
                            </div>
                          }
                        </div>
                      </div>

                      <!-- OUTBOUND -->
                      <div hlmCard class="overflow-hidden" [class.ring-1]="hasOutboundCredentials"
                        [class.ring-green-300]="hasOutboundCredentials">
                        <div class="flex items-center justify-between border-b border-border px-4 py-3">
                          <div class="flex items-center gap-2 text-sm font-medium">
                            <ng-icon hlm name="lucideArrowUpCircle" class="size-4 shrink-0 text-accent" />OUTBOUND Credentials
                          </div>
                          <span hlmBadge class="bg-stone-100 text-stone-600 ring-1 ring-stone-200 text-xs">Manual</span>
                        </div>
                        <div class="p-4">
                          @if (hasOutboundCredentials) {
                            <div class="space-y-2 text-sm">
                              <div><span class="text-xs text-muted-foreground">API Base URL</span>
                                <div class="text-green-700">{{ selectedClient.apiBaseUrl || 'Not configured' }}</div></div>
                              <div><span class="text-xs text-muted-foreground">Client Account ID</span>
                                <div class="text-green-700">{{ selectedClient.clientAccountId || 'Not configured' }}</div></div>
                              <div><span hlmBadge class="bg-green-50 text-green-700 ring-1 ring-green-200 text-xs">Configured</span></div>
                            </div>
                          } @else {
                            <div class="flex flex-col items-center gap-2 py-4 text-center text-muted-foreground">
                              <ng-icon hlm name="lucideSettings" class="size-8" />
                              <p class="text-sm font-medium">Configure OUTBOUND</p>
                              <p class="text-xs">Set up credentials for sending data to external systems</p>
                            </div>
                          }
                          <div class="mt-3 text-center">
                            <button type="button" hlmBtn size="sm" [attr.variant]="hasOutboundCredentials ? 'outline' : undefined"
                              (click)="showOutboundAuthenticationModal(selectedClient)">
                              <ng-icon hlm [name]="hasOutboundCredentials ? 'lucideSettings' : 'lucidePlusCircle'" class="size-4 shrink-0" />
                              {{ hasOutboundCredentials ? 'Update OUTBOUND' : 'Configure OUTBOUND' }}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Connection Test Status -->
                    <div hlmCard>
                      <div class="border-b border-border px-4 py-3 text-sm font-medium flex items-center gap-2">
                        <ng-icon hlm name="lucideWifi" class="size-4 shrink-0 text-accent" />Connection Testing Status
                      </div>
                      <div class="flex flex-wrap gap-6 p-4 text-sm">
                        <div>
                          <p class="text-xs text-muted-foreground mb-1">Last Connection Test</p>
                          <span hlmBadge [class]="selectedClient.lastConnectionTest ? 'bg-green-50 text-green-700 ring-1 ring-green-200' : 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'" class="text-xs">
                            {{ selectedClient.lastConnectionTest ? (selectedClient.lastConnectionTest | date:'short') : 'Not Tested' }}
                          </span>
                        </div>
                        @if (selectedClient.lastConnectionSuccessful !== undefined) {
                          <div>
                            <p class="text-xs text-muted-foreground mb-1">Result</p>
                            <span hlmBadge [class]="selectedClient.lastConnectionSuccessful ? 'bg-green-50 text-green-700 ring-1 ring-green-200' : 'bg-red-50 text-red-700 ring-1 ring-red-200'" class="text-xs">
                              {{ selectedClient.lastConnectionSuccessful ? 'Success' : 'Failed' }}
                            </span>
                          </div>
                        }
                        @if (selectedClient.lastConnectionError) {
                          <div>
                            <p class="text-xs text-muted-foreground mb-1">Last Error</p>
                            <span class="text-xs text-red-600">{{ selectedClient.lastConnectionError }}</span>
                          </div>
                        }
                      </div>
                    </div>

                    <!-- Quick Actions -->
                    <div hlmCard>
                      <div class="border-b border-border px-4 py-3 text-sm font-medium flex items-center gap-2">
                        <ng-icon hlm name="lucideZap" class="size-4 shrink-0 text-accent" />Credential Actions
                      </div>
                      <div class="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
                        <div class="space-y-2">
                          <p class="text-xs font-semibold text-green-700 uppercase tracking-wide">INBOUND</p>
                          @if (!clientCredentials) {
                            <button type="button" hlmBtn size="sm" (click)="generateCredentials()" [disabled]="isSaving">
                              @if (isSaving) { <span class="me-1.5 h-3 w-3 animate-spin rounded-full border-2 border-white/30 border-t-white"></span> }
                              @if (!isSaving) { <ng-icon hlm name="lucideWand2" class="size-4 shrink-0" /> }Generate API Credentials
                            </button>
                          } @else {
                            <button type="button" hlmBtn size="sm" variant="outline" (click)="regenerateCredentials()" [disabled]="isSaving">
                              @if (isSaving) { <span class="me-1.5 h-3 w-3 animate-spin rounded-full border-2 border-border border-t-foreground"></span> }
                              @if (!isSaving) { <ng-icon hlm name="lucideRefreshCw" class="size-4 shrink-0" /> }Regenerate API Credentials
                            </button>
                          }
                          <button type="button" hlmBtn size="sm" variant="outline"
                            (click)="showAuthenticationModalForm(selectedClient)" [disabled]="isSaving">
                            <ng-icon hlm name="lucideShieldCheck" class="size-4 shrink-0" />Authentication Management
                          </button>
                        </div>
                        <div class="space-y-2">
                          <p class="text-xs font-semibold text-ds-accent uppercase tracking-wide">OUTBOUND</p>
                          <button type="button" hlmBtn size="sm" variant="outline"
                            (click)="showOutboundAuthenticationModal(selectedClient)" [disabled]="isSaving">
                            <ng-icon hlm name="lucideSettings" class="size-4 shrink-0" />Configure OUTBOUND
                          </button>
                          <button type="button" hlmBtn size="sm" variant="outline"
                            (click)="testConnection(selectedClient)" [disabled]="isTesting">
                            @if (isTesting) { <span class="me-1.5 h-3 w-3 animate-spin rounded-full border-2 border-border border-t-foreground"></span> }
                            @if (!isTesting) { <ng-icon hlm name="lucideWifi" class="size-4 shrink-0" /> }Test Connection
                          </button>
                        </div>
                      </div>
                    </div>

                    <div class="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50/50 px-4 py-3 text-xs text-blue-700">
                      <ng-icon hlm name="lucideInfo" class="size-4 mt-0.5 shrink-0" />
                      All credentials are encrypted at rest and transmitted securely.
                    </div>
                  }
                </div>
              }

              <!-- ENDPOINTS TAB -->
              @case ('endpoints') {
                <div class="space-y-4">
                  <div class="flex items-center justify-between">
                    <h3 class="text-sm font-semibold">API Endpoints &amp; System Feature Mapping</h3>
                    <div class="flex gap-2">
                      <button type="button" hlmBtn size="sm" variant="outline" (click)="showEndpointModal = true">
                        <ng-icon hlm name="lucidePlus" class="size-4 shrink-0" />Add Endpoint
                      </button>
                      <button type="button" hlmBtn size="sm" (click)="saveEndpointMappings()"
                        [disabled]="isSaving || !hasUnsavedEndpointChanges()">
                        @if (isSaving) { <span class="me-1.5 h-3 w-3 animate-spin rounded-full border-2 border-white/30 border-t-white"></span> }
                        {{ isSaving ? 'Saving...' : 'Save Mappings' }}
                      </button>
                    </div>
                  </div>

                  @if (clientEndpoints.length > 0) {
                    <!-- Stats row -->
                    <div class="grid grid-cols-4 gap-3">
                      @for (stat of [
                        {label:'Total',   value: clientEndpoints.length,       cls:'text-foreground'},
                        {label:'Mapped',  value: getMappedFeaturesCount(),      cls:'text-green-700'},
                        {label:'Unmapped',value: getUnmappedEndpointsCount(),   cls:'text-amber-700'},
                        {label:'Active',  value: getActiveEndpointCount(),      cls:'text-ds-accent'}
                      ]; track stat.label) {
                        <div hlmCard class="p-3 text-center">
                          <div class="text-xs text-muted-foreground">{{ stat.label }}</div>
                          <div class="text-lg font-bold" [class]="stat.cls">{{ stat.value }}</div>
                        </div>
                      }
                    </div>
                  }

                  @if (hasUnsavedEndpointChanges()) {
                    <div class="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50/50 px-3 py-2 text-xs text-blue-700">
                      <ng-icon hlm name="lucideInfo" class="size-4 shrink-0" />
                      Unsaved mapping changes. Click "Save Mappings" to persist.
                    </div>
                  }

                  @if (clientEndpoints.length === 0) {
                    <div class="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground">
                      <ng-icon hlm name="lucideLink" class="size-8" />
                      <p class="text-sm font-medium">No endpoints configured</p>
                      <p class="text-xs">Add endpoints to enable system feature mapping.</p>
                    </div>
                  } @else {
                    <div class="overflow-hidden rounded-md border border-border">
                      <table class="w-full text-sm">
                        <thead class="bg-stone-50 text-xs font-medium text-muted-foreground">
                          <tr>
                            <th class="px-3 py-2.5 text-left">Endpoint</th>
                            <th class="px-3 py-2.5 text-left">Path</th>
                            <th class="px-3 py-2.5 text-left">Method</th>
                            <th class="px-3 py-2.5 text-left">System Feature</th>
                            <th class="px-3 py-2.5 text-left">Status</th>
                            <th class="w-16 px-3 py-2.5"></th>
                          </tr>
                        </thead>
                        <tbody class="divide-y divide-border">
                          @for (endpoint of clientEndpoints; track endpoint.id) {
                            <tr class="h-10 even:bg-stone-50/60 hover:bg-stone-100/70" [class]="getEndpointRowClass(endpoint)">
                              <td class="px-3">
                                <div class="flex items-center gap-2">
                                  <span>{{ endpoint.endpointName }}</span>
                                  <span hlmBadge [class]="getMappingStatusBadgeClass(endpoint)" class="text-[11px]">
                                    {{ getMappingStatusLabel(endpoint) }}
                                  </span>
                                </div>
                              </td>
                              <td class="px-3"><code class="text-xs">{{ endpoint.endpointPath }}</code></td>
                              <td class="px-3">
                                <span hlmBadge class="bg-ds-accent/10 text-ds-accent ring-1 ring-ds-accent/20 text-xs">{{ endpoint.httpMethod }}</span>
                              </td>
                              <td class="px-3">
                                @if (isEndpointMapped(endpoint) && !isEditingEndpoint(endpoint.id)) {
                                  <div class="flex items-center justify-between gap-2">
                                    <div>
                                      <div class="flex items-center gap-1 text-xs font-medium">
                                        <ng-icon hlm [name]="getSystemFeatureIcon(endpoint.systemFeature)" class="size-3.5 shrink-0" />
                                        {{ getSystemFeatureLabel(endpoint.systemFeature) }}
                                      </div>
                                      <div class="text-[11px] text-muted-foreground">{{ getSystemFeatureDescription(endpoint.systemFeature) }}</div>
                                    </div>
                                    <button type="button" hlmBtn variant="ghost" size="sm" class="h-6 w-6 p-0"
                                      (click)="startEditingEndpoint(endpoint.id)">
                                      <ng-icon hlm name="lucidePencil" class="size-3.5" />
                                    </button>
                                  </div>
                                } @else {
                                  <div class="space-y-1">
                                    <select class="h-7 w-full rounded border border-border bg-background px-2 text-xs"
                                      [value]="endpoint.systemFeature || ''"
                                      (change)="onEndpointFeatureChange(endpoint, $event)">
                                      <option value="">Select feature...</option>
                                      @for (feature of systemFeatureOptions; track feature.value) {
                                        <option [value]="feature.value" [disabled]="isFeatureAlreadyMapped(feature.value, endpoint.id)">
                                          {{ feature.label }}{{ isFeatureAlreadyMapped(feature.value, endpoint.id) ? ' (mapped)' : '' }}
                                        </option>
                                      }
                                    </select>
                                    @if (isEditingEndpoint(endpoint.id)) {
                                      <div class="flex gap-1">
                                        <button type="button" hlmBtn size="sm" class="h-6 text-xs" (click)="stopEditingEndpoint(endpoint.id)">Done</button>
                                        <button type="button" hlmBtn variant="outline" size="sm" class="h-6 text-xs" (click)="cancelEndpointEdit(endpoint)">Cancel</button>
                                      </div>
                                    }
                                  </div>
                                }
                              </td>
                              <td class="px-3">
                                <span hlmBadge [class]="endpoint.isActive ? 'bg-green-50 text-green-700 ring-1 ring-green-200' : 'bg-stone-100 text-stone-600 ring-1 ring-stone-200'" class="text-xs">
                                  {{ endpoint.isActive ? 'Active' : 'Inactive' }}
                                </span>
                              </td>
                              <td class="px-3">
                                <button type="button" hlmBtn variant="ghost" size="sm" class="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                  (click)="deleteEndpoint(endpoint.id)" title="Remove endpoint">
                                  <ng-icon hlm name="lucideTrash2" class="size-3.5" />
                                </button>
                              </td>
                            </tr>
                          }
                        </tbody>
                      </table>
                    </div>
                  }
                </div>
              }

              <!-- USERS TAB -->
              @case ('users') {
                <div class="space-y-4">
                  <div class="flex items-center justify-between">
                    <h3 class="text-sm font-semibold">Linked Users</h3>
                    <button type="button" hlmBtn size="sm" variant="outline" (click)="showInviteUserModal(selectedClient)">
                      <ng-icon hlm name="lucideUserPlus" class="size-4 shrink-0" />Link User
                    </button>
                  </div>

                  @if (clientUsers.length === 0) {
                    <div class="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground">
                      <ng-icon hlm name="lucideUsers" class="size-8" />
                      <p class="text-sm">No users linked to this integration</p>
                    </div>
                  } @else {
                    <div class="overflow-hidden rounded-md border border-border">
                      <table class="w-full text-sm">
                        <thead class="bg-stone-50 text-xs font-medium text-muted-foreground">
                          <tr>
                            <th class="px-3 py-2.5 text-left">User</th>
                            <th class="px-3 py-2.5 text-left">Email</th>
                            <th class="px-3 py-2.5 text-left">Role</th>
                            <th class="px-3 py-2.5 text-left">Status</th>
                            <th class="px-3 py-2.5 text-left">Linked</th>
                            <th class="w-16 px-3 py-2.5"></th>
                          </tr>
                        </thead>
                        <tbody class="divide-y divide-border">
                          @for (user of clientUsers; track user.userId) {
                            <tr class="h-10 even:bg-stone-50/60 hover:bg-stone-100/70">
                              <td class="px-3 font-medium">{{ user.username }}</td>
                              <td class="px-3 text-muted-foreground">N/A</td>
                              <td class="px-3">
                                <span hlmBadge class="bg-ds-accent/10 text-ds-accent ring-1 ring-ds-accent/20 text-xs">
                                  {{ user.permissions.join(', ') || 'No permissions' }}
                                </span>
                              </td>
                              <td class="px-3">
                                <span hlmBadge class="bg-green-50 text-green-700 ring-1 ring-green-200 text-xs">Active</span>
                              </td>
                              <td class="px-3 text-xs text-muted-foreground">{{ user.linkedAtUtc | date:'short' }}</td>
                              <td class="px-3">
                                <button type="button" hlmBtn variant="ghost" size="sm" class="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                  (click)="removeLinkedUser(selectedClient.id, user.userId)" title="Remove user">
                                  <ng-icon hlm name="lucideUserMinus" class="size-3.5" />
                                </button>
                              </td>
                            </tr>
                          }
                        </tbody>
                      </table>
                    </div>
                  }
                </div>
              }

              <!-- TESTING TAB -->
              @case ('testing') {
                <app-client-integration-testing [clientId]="selectedClient.id" [client]="selectedClient" />
              }

              <!-- AUDIT TAB -->
              @case ('audit') {
                <app-client-audit-logs [clientId]="selectedClient.id" />
              }
            }
          </div>
        }

        <!-- Create Mode (no tabs) -->
        @if (showCreateClient) {
          <div class="p-6">
            <form [formGroup]="clientForm" (ngSubmit)="saveClient()" class="space-y-5">
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div class="space-y-1.5">
                  <label class="text-sm font-medium">Client Name <span class="text-red-500">*</span></label>
                  <input type="text" formControlName="clientName"
                    class="h-9 w-full rounded-md border px-3 text-sm"
                    [class]="hasFieldError(clientForm,'clientName') ? 'border-red-400 bg-red-50' : 'border-border-strong bg-background'"
                    placeholder="Enter client name">
                  @if (hasFieldError(clientForm,'clientName')) {
                    <p class="text-xs text-red-600">{{ getFieldError(clientForm,'clientName') }}</p>
                  }
                </div>
                <div class="space-y-1.5">
                  <label class="text-sm font-medium">Client Type <span class="text-red-500">*</span></label>
                  <select formControlName="clientType"
                    class="h-9 w-full rounded-md border px-3 text-sm border-border-strong bg-background">
                    @for (opt of providerOptions; track opt.value) {
                      <option [value]="opt.value">{{ opt.label }}</option>
                    }
                  </select>
                </div>
              </div>

              <div class="space-y-1.5">
                <label class="text-sm font-medium">Description <span class="text-red-500">*</span></label>
                <textarea formControlName="description" rows="3"
                  class="w-full rounded-md border px-3 py-2 text-sm"
                  [class]="hasFieldError(clientForm,'description') ? 'border-red-400 bg-red-50' : 'border-border-strong bg-background'"
                  placeholder="Describe the integration purpose and configuration"></textarea>
                @if (hasFieldError(clientForm,'description')) {
                  <p class="text-xs text-red-600">{{ getFieldError(clientForm,'description') }}</p>
                }
              </div>

              <div class="rounded-lg border border-blue-200 bg-blue-50/50 px-4 py-3 text-xs text-blue-700">
                <strong>Credential Configuration:</strong> After creating, configure INBOUND (generate API credentials) and OUTBOUND (external API access) in the Credentials tab.
              </div>

              <!-- Endpoints in create mode -->
              <div class="space-y-3">
                <div class="flex items-center justify-between">
                  <h4 class="text-sm font-medium">Endpoints</h4>
                  <button type="button" hlmBtn size="sm" variant="outline" (click)="showEndpointModal = true">
                    <ng-icon hlm name="lucidePlusCircle" class="size-4 shrink-0" />Add Endpoint
                  </button>
                </div>
                @if (newClientEndpoints.length === 0) {
                  <div class="rounded-lg border border-dashed border-border py-8 text-center text-sm text-muted-foreground">
                    No endpoints configured. Add at least one endpoint.
                  </div>
                } @else {
                  <div class="overflow-hidden rounded-md border border-border">
                    <table class="w-full text-sm">
                      <thead class="bg-stone-50 text-xs font-medium text-muted-foreground">
                        <tr>
                          <th class="px-3 py-2.5 text-left">Name</th>
                          <th class="px-3 py-2.5 text-left">Path</th>
                          <th class="px-3 py-2.5 text-left">Method</th>
                          <th class="px-3 py-2.5 text-left">Status</th>
                          <th class="w-16 px-3 py-2.5"></th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-border">
                        @for (endpoint of newClientEndpoints; track $index; let i = $index) {
                          <tr class="h-10 even:bg-stone-50/60">
                            <td class="px-3">{{ endpoint.endpointName }}</td>
                            <td class="px-3 text-xs text-muted-foreground break-all">{{ endpoint.endpointPath }}</td>
                            <td class="px-3">
                              <span hlmBadge class="bg-blue-50 text-blue-700 ring-1 ring-blue-200 text-xs">{{ endpoint.httpMethod }}</span>
                            </td>
                            <td class="px-3">
                              <span hlmBadge [class]="endpoint.isActive ? 'bg-green-50 text-green-700 ring-1 ring-green-200' : 'bg-stone-100 text-stone-600 ring-1 ring-stone-200'" class="text-xs">
                                {{ endpoint.isActive ? 'Active' : 'Inactive' }}
                              </span>
                            </td>
                            <td class="px-3">
                              <button type="button" hlmBtn variant="ghost" size="sm" class="h-7 w-7 p-0 text-red-500"
                                (click)="removeNewEndpoint(i)">
                                <ng-icon hlm name="lucideTrash2" class="size-3.5" />
                              </button>
                            </td>
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                }
              </div>

              <div class="flex justify-end gap-2 pt-2">
                <button type="button" hlmBtn variant="outline" (click)="cancelClientForm()" [disabled]="isSaving">Cancel</button>
                <button type="submit" hlmBtn [disabled]="isSaving">
                  @if (isSaving) { <span class="me-2 h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white"></span> }
                  {{ isSaving ? 'Creating...' : 'Create Integration' }}
                </button>
              </div>
            </form>
          </div>
        }
      </div>
    }

    <!-- Integration Cards Grid -->
    @if (!isLoading && !showCreateClient && !showEditClient) {
      @if (integrationClients.length === 0) {
        <div class="flex flex-col items-center gap-3 py-20 text-center text-muted-foreground">
          <ng-icon hlm name="lucideLink" class="size-12" />
          <h3 class="text-lg font-semibold text-foreground">No Client Integrations</h3>
          <p class="text-sm">Create your first client integration to get started.</p>
          <button type="button" hlmBtn (click)="showCreateClientForm()">
            <ng-icon hlm name="lucidePlusCircle" class="size-4 shrink-0" />Add First Integration
          </button>
        </div>
      } @else {
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          @for (client of integrationClients; track client.id) {
            <div hlmCard class="flex flex-col overflow-hidden">
              <div class="flex items-start justify-between border-b border-border px-4 py-3">
                <div>
                  <div class="flex items-center gap-2 font-medium text-sm">
                    <ng-icon hlm [name]="getProviderIcon(client.clientType)" class="size-4 shrink-0 text-accent" />
                    {{ client.clientName }}
                  </div>
                  <p class="text-xs text-muted-foreground mt-0.5">{{ getClientTypeLabel(client.clientType) }}</p>
                </div>
                <!-- Row actions -->
                <div class="flex items-center gap-0.5">
                  <button type="button" hlmBtn variant="ghost" size="sm" class="h-7 w-7 p-0"
                    (click)="showEditClientForm(client)" title="Edit">
                    <ng-icon hlm name="lucidePencil" class="size-3.5" />
                  </button>
                  <button type="button" hlmBtn variant="ghost" size="sm" class="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                    (click)="deleteClient(client)" title="Delete">
                    <ng-icon hlm name="lucideTrash2" class="size-3.5" />
                  </button>
                  <!-- More actions menu -->
                  <div class="relative">
                    <button type="button" hlmBtn variant="ghost" size="sm" class="h-7 w-7 p-0"
                      (click)="openMoreMenuId = openMoreMenuId === client.id ? null : client.id; $event.stopPropagation()"
                      title="More actions">
                      <ng-icon hlm name="lucideMoreVertical" class="size-3.5" />
                    </button>
                    @if (openMoreMenuId === client.id) {
                      <div class="absolute right-0 top-full z-30 mt-1 min-w-[170px] rounded-md border border-border bg-popover shadow-lg"
                        (click)="$event.stopPropagation()">
                        <button type="button"
                          class="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-accent/10 rounded-t-md"
                          (click)="showIntegrationTesting(client); openMoreMenuId = null">
                          <ng-icon hlm name="lucidePlayCircle" class="size-3.5 shrink-0 text-accent" />Test Integration
                        </button>
                      </div>
                    }
                  </div>
                </div>
              </div>

              <div class="flex-1 p-4 space-y-3">
                <p class="text-xs text-muted-foreground line-clamp-2">{{ client.description }}</p>
                <div class="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p class="text-muted-foreground mb-1">Status</p>
                    <span hlmBadge [class]="getStatusBadgeClass(client.status)" class="text-xs">{{ getStatusLabel(client.status) }}</span>
                  </div>
                  <div>
                    <p class="text-muted-foreground mb-1">Users</p>
                    <span class="font-medium">{{ client.linkedUsers.length || 0 }}</span>
                  </div>
                  <div>
                    <p class="text-muted-foreground mb-1">Created</p>
                    <span>{{ client.createdAtUtc | date:'short' }}</span>
                  </div>
                  <div>
                    <p class="text-muted-foreground mb-1">Last Sync</p>
                    <span>{{ client.lastConnectionTest ? (client.lastConnectionTest | date:'short') : 'Never' }}</span>
                  </div>
                </div>
              </div>

              <div class="border-t border-border p-3 flex gap-2">
                <button type="button" hlmBtn variant="outline" size="sm" class="flex-1"
                  (click)="showEditClientForm(client)">
                  <ng-icon hlm name="lucideSettings" class="size-4 shrink-0" />Manage
                </button>
                <button type="button" hlmBtn variant="outline" size="sm"
                  (click)="testConnection(client)" [disabled]="isTesting">
                  @if (isTesting) { <span class="me-1 h-3 w-3 animate-spin rounded-full border-2 border-border border-t-foreground"></span> }
                  @if (!isTesting) { <ng-icon hlm name="lucideWifi" class="size-4 shrink-0" /> }Test
                </button>
              </div>
            </div>
          }
        </div>
      }
    }

  </div>
</div>

<!-- ══════════════════════════════════════════════════════════════
     MODALS
══════════════════════════════════════════════════════════════ -->

<!-- Invite User Modal -->
@if (showInviteUser) {
  <div class="fixed inset-0 z-50 grid place-items-center p-4" (keydown.escape)="cancelInviteUser()">
    <div class="absolute inset-0 bg-stone-900/40 backdrop-blur-[2px]" (click)="cancelInviteUser()"></div>
    <div hlmCard class="relative z-10 w-full max-w-md shadow-xl">
      <div class="flex items-center justify-between border-b border-border px-5 py-4">
        <h3 class="text-base font-semibold flex items-center gap-2">
          <ng-icon hlm name="lucideUserPlus" class="size-4 shrink-0 text-accent" />Link User to Integration
        </h3>
        <button type="button" hlmBtn variant="ghost" size="sm" class="h-7 w-7 p-0" (click)="cancelInviteUser()">
          <ng-icon hlm name="lucideX" class="size-4" />
        </button>
      </div>
      <form [formGroup]="inviteUserForm" (ngSubmit)="sendInvitation()">
        <div class="space-y-4 p-5">
          @for (field of [
            {ctrl:'userId',   label:'User ID',   type:'text',  ph:'Enter user ID'},
            {ctrl:'userName', label:'User Name', type:'text',  ph:'Enter user name'},
            {ctrl:'email',    label:'Email',     type:'email', ph:'user@example.com'}
          ]; track field.ctrl) {
            <div class="space-y-1.5">
              <label class="text-sm font-medium">{{ field.label }} <span class="text-red-500">*</span></label>
              <input [type]="field.type" [formControlName]="field.ctrl"
                class="h-9 w-full rounded-md border px-3 text-sm"
                [class]="hasFieldError(inviteUserForm, field.ctrl) ? 'border-red-400 bg-red-50' : 'border-border-strong bg-background'"
                [placeholder]="field.ph">
              @if (hasFieldError(inviteUserForm, field.ctrl)) {
                <p class="text-xs text-red-600">{{ getFieldError(inviteUserForm, field.ctrl) }}</p>
              }
            </div>
          }
          <div class="space-y-1.5">
            <label class="text-sm font-medium">Role <span class="text-red-500">*</span></label>
            <select formControlName="role"
              class="h-9 w-full rounded-md border border-border-strong bg-background px-3 text-sm">
              @for (role of roleOptions; track role.value) {
                <option [value]="role.value">{{ role.label }}</option>
              }
            </select>
            <p class="text-xs text-muted-foreground">{{ getSelectedRoleDescription() }}</p>
          </div>
        </div>
        <div class="flex justify-end gap-2 border-t border-border px-5 py-4">
          <button type="button" hlmBtn variant="outline" (click)="cancelInviteUser()" [disabled]="isSaving">Cancel</button>
          <button type="submit" hlmBtn [disabled]="isSaving">
            @if (isSaving) { <span class="me-2 h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white"></span> }
            @if (!isSaving) { <ng-icon hlm name="lucideUserPlus" class="size-4 shrink-0" /> }Link User
          </button>
        </div>
      </form>
    </div>
  </div>
}

<!-- Endpoint Modal -->
@if (showEndpointModal) {
  <div class="fixed inset-0 z-50 grid place-items-center p-4" (keydown.escape)="cancelEndpointModal()">
    <div class="absolute inset-0 bg-stone-900/40 backdrop-blur-[2px]" (click)="cancelEndpointModal()"></div>
    <div hlmCard class="relative z-10 w-full max-w-lg shadow-xl">
      <div class="flex items-center justify-between border-b border-border px-5 py-4">
        <h3 class="text-base font-semibold flex items-center gap-2">
          <ng-icon hlm name="lucideGlobe" class="size-4 shrink-0 text-accent" />Add Endpoint
        </h3>
        <button type="button" hlmBtn variant="ghost" size="sm" class="h-7 w-7 p-0" (click)="cancelEndpointModal()">
          <ng-icon hlm name="lucideX" class="size-4" />
        </button>
      </div>
      <form [formGroup]="endpointForm" (ngSubmit)="saveEndpoint()">
        <div class="space-y-4 p-5">
          <div class="space-y-1.5">
            <label class="text-sm font-medium">Endpoint Name <span class="text-red-500">*</span></label>
            <input type="text" formControlName="endpointName"
              class="h-9 w-full rounded-md border px-3 text-sm"
              [class]="hasFieldError(endpointForm,'endpointName') ? 'border-red-400 bg-red-50' : 'border-border-strong bg-background'"
              placeholder="Enter endpoint name">
            @if (hasFieldError(endpointForm,'endpointName')) {
              <p class="text-xs text-red-600">{{ getFieldError(endpointForm,'endpointName') }}</p>
            }
          </div>
          <div class="space-y-1.5">
            <label class="text-sm font-medium">Endpoint Path <span class="text-red-500">*</span></label>
            <input type="text" formControlName="endpointPath"
              class="h-9 w-full rounded-md border px-3 text-sm"
              [class]="hasFieldError(endpointForm,'endpointPath') ? 'border-red-400 bg-red-50' : 'border-border-strong bg-background'"
              placeholder="/api/webhook/orders">
            <p class="text-xs text-muted-foreground">Path should start with /</p>
            @if (hasFieldError(endpointForm,'endpointPath')) {
              <p class="text-xs text-red-600">{{ getFieldError(endpointForm,'endpointPath') }}</p>
            }
          </div>
          <div class="space-y-1.5">
            <label class="text-sm font-medium">HTTP Method <span class="text-red-500">*</span></label>
            <select formControlName="httpMethod"
              class="h-9 w-full rounded-md border border-border-strong bg-background px-3 text-sm">
              @for (method of httpMethods; track method.value) {
                <option [value]="method.value">{{ method.label }}</option>
              }
            </select>
          </div>
          <div class="space-y-1.5">
            <label class="text-sm font-medium">Description</label>
            <textarea formControlName="description" rows="2"
              class="w-full rounded-md border border-border-strong bg-background px-3 py-2 text-sm"
              placeholder="Optional description"></textarea>
          </div>
          <div class="space-y-1.5">
            <label class="text-sm font-medium">System Feature Mapping</label>
            <select formControlName="systemFeature"
              class="h-9 w-full rounded-md border border-border-strong bg-background px-3 text-sm">
              <option value="">Not mapped to any feature</option>
              @for (feature of systemFeatureOptions; track feature.value) {
                <option [value]="feature.value" [disabled]="isFeatureAlreadyMapped(feature.value)">
                  {{ feature.label }}{{ isFeatureAlreadyMapped(feature.value) ? ' (Already mapped)' : '' }}
                </option>
              }
            </select>
            <p class="text-xs text-muted-foreground">Each feature can only be mapped to one active endpoint.</p>
            @if (endpointForm.get('systemFeature')?.value) {
              <div class="rounded border border-blue-200 bg-blue-50/50 px-3 py-2 text-xs text-blue-700">
                <strong>{{ getSystemFeatureLabel(endpointForm.get('systemFeature')?.value) }}:</strong>
                {{ getSystemFeatureDescription(endpointForm.get('systemFeature')?.value) }}
              </div>
            }
          </div>
          <div class="flex items-center gap-2">
            <input type="checkbox" formControlName="isActive" id="endpointActive"
              class="h-4 w-4 rounded border-border accent-ds-accent">
            <label for="endpointActive" class="text-sm">Active endpoint</label>
          </div>
        </div>
        <div class="flex justify-end gap-2 border-t border-border px-5 py-4">
          <button type="button" hlmBtn variant="outline" (click)="cancelEndpointModal()" [disabled]="isSaving">Cancel</button>
          <button type="submit" hlmBtn [disabled]="isSaving">
            @if (isSaving) { <span class="me-2 h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white"></span> }
            @if (!isSaving) { <ng-icon hlm name="lucidePlusCircle" class="size-4 shrink-0" /> }Add Endpoint
          </button>
        </div>
      </form>
    </div>
  </div>
}

<!-- Secrets / Authentication Management Modal -->
@if (showSecretsModal) {
  <div class="fixed inset-0 z-50 grid place-items-center p-4"
    (keydown.escape)="cancelSecretsModal()"
    (click)="onSecretsModalBackdropClick($event)"
    role="dialog" aria-labelledby="secretsModalTitle">
    <div class="absolute inset-0 bg-stone-900/40 backdrop-blur-[2px]"></div>
    <div hlmCard class="relative z-10 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
      <div class="flex items-center justify-between border-b border-border px-5 py-4">
        <h3 id="secretsModalTitle" class="text-base font-semibold flex items-center gap-2">
          <ng-icon hlm name="lucideShieldCheck" class="size-4 shrink-0 text-accent" />
          Authentication Management
          <span hlmBadge
            [class]="selectedCredentialDirection === 'INBOUND'
              ? 'bg-green-50 text-green-700 ring-1 ring-green-200'
              : 'bg-ds-accent/10 text-ds-accent ring-1 ring-ds-accent/20'">
            {{ selectedCredentialDirection }}
          </span>
        </h3>
        <button type="button" hlmBtn variant="ghost" size="sm" class="h-7 w-7 p-0" (click)="cancelSecretsModal()">
          <ng-icon hlm name="lucideX" class="size-4" />
        </button>
      </div>

      <form [formGroup]="secretsForm" (ngSubmit)="saveCredentialChanges()">
        <div class="space-y-5 p-5">

          <!-- Security notice -->
          <div class="flex items-start gap-2 rounded-lg border px-4 py-3 text-sm"
            [class]="isAutoPopulatedInboundCredentials()
              ? 'border-green-200 bg-green-50 text-green-800'
              : 'border-blue-200 bg-blue-50 text-blue-800'">
            <ng-icon hlm name="lucideShieldCheck" class="size-4 mt-0.5 shrink-0" />
            <div>
              <strong>{{ isAutoPopulatedInboundCredentials() ? 'Auto-Generated Credentials:' : 'Enhanced Credential Management:' }}</strong>
              @if (isAutoPopulatedInboundCredentials()) {
                INBOUND credentials are automatically populated from generated API credentials and are read-only.
              } @else {
                Configure {{ selectedCredentialDirection }} credentials for secure integration. All credentials are encrypted.
              }
            </div>
          </div>

          <!-- Auto-populate banner -->
          @if (selectedCredentialDirection === 'INBOUND' && hasGeneratedCredentials) {
            <div class="flex items-start gap-3 rounded-lg border border-ds-accent/30 bg-ds-accent/5 px-4 py-3">
              <ng-icon hlm name="lucideWand2" class="size-5 shrink-0 mt-0.5 text-accent" />
              <div class="space-y-2">
                <p class="text-sm font-medium">INBOUND Credentials Available</p>
                <p class="text-xs text-muted-foreground">Generated API credentials detected. INBOUND credentials can be auto-populated.</p>
                @if (!inboundCredentialsPopulated) {
                  <button type="button" hlmBtn size="sm" (click)="autoPopulateInboundCredentials()">
                    <ng-icon hlm name="lucideWand2" class="size-4 shrink-0" />Auto-Populate
                  </button>
                } @else {
                  <span hlmBadge class="bg-green-50 text-green-700 ring-1 ring-green-200 text-xs">
                    <ng-icon hlm name="lucideCheckCircle" class="size-3.5 shrink-0" />Auto-Populated
                  </span>
                }
              </div>
            </div>
          }

          <!-- INBOUND API Credential Management -->
          @if (selectedCredentialDirection === 'INBOUND') {
            <div hlmCard class="overflow-hidden">
              <div class="flex items-center gap-2 border-b border-border bg-stone-50/60 px-4 py-3 text-sm font-medium">
                <ng-icon hlm name="lucideKey" class="size-4 shrink-0 text-blue-600" />API Credential Management
                <span hlmBadge class="bg-blue-50 text-blue-700 ring-1 ring-blue-200 text-xs">Inbound Authentication</span>
              </div>
              <div class="p-4">
                @if (!clientCredentials) {
                  <div class="flex flex-col items-center gap-3 py-8 text-center text-muted-foreground">
                    <ng-icon hlm name="lucideShieldPlus" class="size-8" />
                    <p class="text-sm font-medium text-foreground">No API Credentials Generated</p>
                    <p class="text-xs">Generate credentials to allow this client to authenticate.</p>
                    <button type="button" hlmBtn (click)="generateCredentials()" [disabled]="isSaving">
                      @if (isSaving) { <span class="me-2 h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white"></span> }
                      @if (!isSaving) { <ng-icon hlm name="lucideWand2" class="size-4 shrink-0" /> }Generate API Credentials
                    </button>
                  </div>
                } @else {
                  <div class="space-y-3">
                    <div class="grid grid-cols-2 gap-3">
                      <div class="space-y-1.5">
                        <label class="text-xs font-medium">API Account</label>
                        <div class="flex gap-1">
                          <input type="text" class="h-8 flex-1 rounded border border-border bg-stone-50 px-2 text-xs"
                            [value]="clientCredentials.apiAccount" readonly>
                          <button type="button" hlmBtn variant="outline" size="sm" class="h-8 px-2"
                            (click)="copyToClipboard(clientCredentials.apiAccount, 'API Account')">
                            <ng-icon hlm name="lucideClipboard" class="size-3.5" />
                          </button>
                        </div>
                      </div>
                      <div class="space-y-1.5">
                        <label class="text-xs font-medium">Status</label>
                        <div class="flex h-8 items-center">
                          <span hlmBadge [class]="clientCredentials.isActive ? 'bg-green-50 text-green-700 ring-1 ring-green-200' : 'bg-red-50 text-red-700 ring-1 ring-red-200'" class="text-xs">
                            {{ clientCredentials.isActive ? 'Active' : 'Inactive' }}
                          </span>
                        </div>
                      </div>
                    </div>

                    <!-- JWT Secret field (INBOUND JWT only) -->
                    @if (selectedCredentialType === 'jwt' && selectedCredentialDirection === 'INBOUND') {
                      <div class="space-y-1.5">
                        <label class="text-xs font-medium">Secret Key</label>
                        <div class="flex gap-1">
                          <input type="text" class="h-8 flex-1 rounded border border-border bg-stone-50 px-2 font-mono text-xs"
                            [value]="jwtSecretDisplayValue" readonly
                            [class.text-green-700]="jwtSecretState.isVisible"
                            [class.text-muted-foreground]="!jwtSecretState.isVisible">
                          <button type="button" hlmBtn variant="outline" size="sm" class="h-8 px-2"
                            (click)="copyJwtSecretToClipboard()" [disabled]="!canCopyJwtSecret">
                            <ng-icon hlm name="lucideClipboard" class="size-3.5" />
                          </button>
                        </div>
                        @if (jwtSecretState.isVisible) {
                          <p class="text-xs text-green-600 flex items-center gap-1"><ng-icon hlm name="lucideCheckCircle" class="size-3.5 shrink-0" />Secret visible ({{ jwtSecretState.countdown }}s)</p>
                        } @else {
                          <p class="text-xs text-muted-foreground flex items-center gap-1"><ng-icon hlm name="lucideInfo" class="size-3.5 shrink-0" />Click "Regenerate JWT Secret" to view</p>
                        }
                      </div>
                    }

                    <div class="flex items-center justify-between pt-1">
                      <p class="text-xs text-muted-foreground">Created: {{ clientCredentials.createdAtUtc | date:'medium' }}</p>
                      <div class="flex gap-1.5">
                        <button type="button" hlmBtn size="sm"
                          [class]="isJwtInboundSelected ? '' : 'border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100'"
                          (click)="regenerateCredentials(); $event.preventDefault(); $event.stopPropagation();"
                          [disabled]="isSaving">
                          @if (isSaving) { <span class="me-1 h-3 w-3 animate-spin rounded-full border-2 border-white/30 border-t-white"></span> }
                          @if (!isSaving) { <ng-icon hlm name="lucideRefreshCw" class="size-4 shrink-0" /> }
                          {{ isJwtInboundSelected ? 'Regenerate JWT Secret' : 'Regenerate' }}
                        </button>
                        <button type="button" hlmBtn variant="outline" size="sm" class="h-8 px-2"
                          (click)="showCurrentJwtSecret(); $event.preventDefault(); $event.stopPropagation();"
                          [disabled]="!hasJwtSecretToShow()" title="Show current JWT secret">
                          <ng-icon hlm name="lucideEye" class="size-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                }

                <!-- New Generated Credentials Display -->
                @if (showCredentialsInfo && generatedCredentials) {
                  <div class="mt-4 space-y-3">
                    <div class="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                      <ng-icon hlm name="lucideAlertTriangle" class="size-4 shrink-0 mt-0.5" />
                      Save these credentials immediately. They will be cleared automatically for security.
                    </div>
                    <div class="grid grid-cols-2 gap-3">
                      <div class="space-y-1.5">
                        <label class="text-xs font-medium">API Account</label>
                        <div class="flex gap-1">
                          <input type="text" class="h-8 flex-1 rounded border border-border bg-stone-50 px-2 text-xs"
                            [value]="generatedCredentials.apiAccount" readonly>
                          <button type="button" hlmBtn variant="outline" size="sm" class="h-8 px-2"
                            (click)="copyToClipboard(generatedCredentials.apiAccount, 'API Account')">
                            <ng-icon hlm name="lucideClipboard" class="size-3.5" />
                          </button>
                        </div>
                      </div>
                      <div class="space-y-1.5">
                        <label class="text-xs font-medium">API Secret Key</label>
                        <div class="flex gap-1">
                          <input type="password" class="h-8 flex-1 rounded border border-border bg-stone-50 px-2 text-xs"
                            [value]="generatedCredentials.apiSecretKey" readonly>
                          <button type="button" hlmBtn variant="outline" size="sm" class="h-8 px-2"
                            (click)="copyToClipboard(generatedCredentials.apiSecretKey, 'API Secret')">
                            <ng-icon hlm name="lucideClipboard" class="size-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                }
                <div class="mt-3 flex items-start gap-2 rounded border border-blue-200 bg-blue-50/50 px-3 py-2 text-xs text-blue-700">
                  <ng-icon hlm name="lucideInfo" class="size-4 shrink-0 mt-0.5" />
                  Credentials will be automatically used for inbound authentication configuration below.
                </div>
              </div>
            </div>
          }

          <!-- JWT Secret Timer Banner (when secret is visible) -->
          @if (jwtSecretState.isVisible && jwtSecretState.secret) {
            <div class="rounded-lg border border-amber-300 bg-amber-50 p-4 space-y-3">
              <div class="flex items-center justify-between">
                <h4 class="text-sm font-semibold text-amber-800 flex items-center gap-2">
                  <ng-icon hlm name="lucideShieldCheck" class="size-4 shrink-0" />JWT Secret Generated
                </h4>
                <span hlmBadge
                  [class]="jwtSecretState.countdown > 10 ? 'bg-amber-100 text-amber-800 ring-1 ring-amber-300' : 'bg-red-50 text-red-700 ring-1 ring-red-300'"
                  class="font-mono text-xs">
                  <ng-icon hlm name="lucideClock" class="size-3.5 shrink-0 inline-block align-middle mr-0.5" />{{ jwtSecretState.countdown }}s
                </span>
              </div>
              <!-- Progress bar -->
              <div class="h-1.5 w-full rounded-full bg-stone-200 overflow-hidden">
                <div class="h-full rounded-full transition-all duration-1000"
                  [class]="jwtSecretState.countdown > 10 ? 'bg-amber-400' : 'bg-red-500'"
                  [style.width.%]="(jwtSecretState.countdown / 30) * 100"></div>
              </div>
              <!-- Secret field -->
              <div class="space-y-2 rounded-md border border-amber-200 bg-white p-3">
                <label class="text-xs font-bold text-amber-700 flex items-center gap-1">
                  <ng-icon hlm name="lucideKey" class="size-3.5 shrink-0" />JWT Secret (Copy Now!)
                </label>
                <div class="flex gap-1.5">
                  <input type="text" class="h-8 flex-1 rounded border border-border font-mono px-2 text-xs"
                    [value]="jwtSecretDisplayValue" readonly
                    [class.text-green-700]="jwtSecretState.isVisible"
                    [class.text-muted-foreground]="!jwtSecretState.isVisible"
                    placeholder="JWT secret will appear here...">
                  <button type="button" hlmBtn variant="outline" size="sm" class="h-8 px-2"
                    (click)="copyJwtSecretToClipboard()" [disabled]="!jwtSecretState.secret">
                    <ng-icon hlm name="lucideClipboard" class="size-3.5" />
                  </button>
                </div>
                <div class="flex items-start gap-2 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">
                  <ng-icon hlm name="lucideAlertTriangle" class="size-4 shrink-0 mt-0.5" />
                  Critical: This secret disappears in {{ jwtSecretState.countdown }}s and cannot be recovered. Copy and store securely immediately.
                </div>
                @if (generatedCredentials?.apiAccount) {
                  <div class="space-y-1 text-xs text-muted-foreground">
                    <p class="font-medium text-foreground">How to use:</p>
                    <ol class="list-decimal ml-4 space-y-0.5">
                      <li>Use account <code class="text-ds-accent">{{ generatedCredentials!.apiAccount }}</code> in JWT header</li>
                      <li>Sign payload with this secret using HMAC256</li>
                      <li>Include in Authorization header: <code>Bearer [your-jwt]</code></li>
                      <li>JWT must include timestamp within 5 minutes</li>
                    </ol>
                  </div>
                }
              </div>
            </div>
          }

          <!-- Credential Direction Selection -->
          <div class="space-y-2">
            <label class="text-sm font-semibold">Credential Direction</label>
            <div class="grid grid-cols-2 gap-3">
              @for (direction of credentialDirections; track direction.value) {
                <div hlmCard class="cursor-pointer p-4 text-center transition-all"
                  [class]="selectedCredentialDirection === direction.value
                    ? 'ring-2 ring-ds-accent border-ds-accent'
                    : 'hover:bg-stone-50'"
                  (click)="onCredentialDirectionChange(direction.value)">
                  <ng-icon hlm [name]="direction.icon" class="size-6"
                    [class]="selectedCredentialDirection === direction.value ? 'text-accent' : 'text-muted-foreground'" />
                  <p class="mt-2 text-sm font-medium"
                    [class]="selectedCredentialDirection === direction.value ? 'text-ds-accent' : ''">
                    {{ direction.label }}
                  </p>
                  <p class="text-xs text-muted-foreground">{{ direction.description }}</p>
                </div>
              }
            </div>
          </div>

          <!-- Auth Type Selection -->
          <div class="space-y-1.5">
            <label class="text-sm font-semibold">Authentication Type <span class="text-red-500">*</span></label>
            <div class="flex items-center gap-2">
              <select formControlName="credentialType"
                class="h-9 flex-1 rounded-md border px-3 text-sm"
                [class]="isAutoPopulatedInboundCredentials() ? 'border-border bg-stone-50' : 'border-border-strong bg-background'">
                @for (type of credentialTypes; track type.value) {
                  <option [value]="type.value">{{ type.label }} - {{ type.description }}</option>
                }
              </select>
              @if (isAutoPopulatedInboundCredentials()) {
                <span hlmBadge class="bg-green-50 text-green-700 ring-1 ring-green-200 text-xs shrink-0">
                  <ng-icon hlm name="lucideBot" class="size-3.5 shrink-0" />Generated
                </span>
              }
            </div>
            @if (isAutoPopulatedInboundCredentials()) {
              <p class="text-xs text-muted-foreground">Auth type is automatically set based on generated credentials.</p>
            }
            @if (!isAutoPopulatedInboundCredentials() && selectedCredentialDirection === 'OUTBOUND') {
              <p class="text-xs text-ds-accent">This type will be used for both credential storage and API authentication.</p>
            }
          </div>

          <!-- Dynamic Credential Fields -->
          @if (selectedCredentialType === 'api_key') {
            <div class="space-y-1.5">
              <label class="text-sm font-medium">API Key <span class="text-red-500">*</span></label>
              <div class="flex items-center gap-2">
                <input type="password" formControlName="apiKey"
                  class="h-9 flex-1 rounded-md border px-3 text-sm"
                  [class]="hasFieldError(secretsForm,'apiKey') ? 'border-red-400 bg-red-50' : (isAutoPopulatedInboundCredentials() ? 'border-border bg-stone-50' : 'border-border-strong bg-background')"
                  [readonly]="isAutoPopulatedInboundCredentials()"
                  placeholder="Enter API key">
                @if (isAutoPopulatedInboundCredentials()) {
                  <span hlmBadge class="bg-blue-50 text-blue-700 ring-1 ring-blue-200 text-xs shrink-0">
                    <ng-icon hlm name="lucideLock" class="size-3.5 shrink-0" />Protected
                  </span>
                }
              </div>
              @if (hasFieldError(secretsForm,'apiKey')) {
                <p class="text-xs text-red-600">{{ getFieldError(secretsForm,'apiKey') }}</p>
              }
            </div>
          }

          @if (selectedCredentialType === 'jwt') {
            @if (selectedCredentialDirection === 'INBOUND') {
              <div class="grid grid-cols-2 gap-4">
                <div class="space-y-1.5">
                  <label class="text-sm font-medium">API Account</label>
                  <div class="flex items-center gap-2">
                    <input type="text" formControlName="clientId"
                      class="h-9 flex-1 rounded-md border border-border-strong bg-background px-3 text-sm"
                      placeholder="Enter API account identifier">
                    @if (isAutoPopulatedInboundCredentials()) {
                      <span hlmBadge class="bg-ds-accent/10 text-ds-accent ring-1 ring-ds-accent/20 text-xs shrink-0">Editable</span>
                    }
                  </div>
                </div>
                <div class="space-y-1.5">
                  <label class="text-sm font-medium">JWT Secret Key <span class="text-red-500">*</span></label>
                  @if (isJwtInboundSelected) {
                    <div class="rounded-lg border border-blue-200 bg-blue-50/50 px-3 py-2 text-xs text-blue-700">
                      <ng-icon hlm name="lucideShieldCheck" class="size-3.5 shrink-0 inline-block align-middle mr-1" />
                      <strong>Auto-Generated JWT Secret.</strong> JWT secrets are generated using cryptographically secure methods.
                      @if (isAutoPopulatedInboundCredentials()) {
                        <br><ng-icon hlm name="lucideCheckCircle" class="size-3.5 shrink-0 inline-block align-middle mr-0.5 text-green-600" />Secret generated and stored securely.
                      } @else {
                        <br><ng-icon hlm name="lucideAlertTriangle" class="size-3.5 shrink-0 inline-block align-middle mr-0.5 text-amber-500" />Click "Authentication Management" to generate.
                      }
                    </div>
                  } @else {
                    <div class="flex items-center gap-2">
                      <input type="password" formControlName="apiKey"
                        class="h-9 flex-1 rounded-md border border-border-strong bg-background px-3 font-mono text-sm"
                        [readonly]="isAutoPopulatedInboundCredentials()"
                        placeholder="Enter secret key">
                    </div>
                  }
                </div>
              </div>
              @if (isAutoPopulatedInboundCredentials()) {
                <div class="rounded border border-blue-200 bg-blue-50/50 px-3 py-2 text-xs text-blue-700">
                  <ng-icon hlm name="lucideInfo" class="size-3.5 shrink-0 inline-block align-middle mr-1" />JWT Usage: Use the API account as the 'account' claim, sign with the secret using HS256. Include standard claims: iss, aud, iat, exp.
                </div>
              }
            } @else {
              <div class="rounded-lg border border-blue-200 bg-blue-50/50 px-4 py-3 text-xs text-blue-700">
                <ng-icon hlm name="lucideInfo" class="size-3.5 shrink-0 inline-block align-middle mr-1" /><strong>JWT OUTBOUND:</strong> Uses the Client Account ID and Client Secret Key configured below. The Account ID is used as the JWT subject, the Secret Key signs the tokens.
              </div>
            }
          }

          @if (selectedCredentialType === 'oauth2') {
            <div class="grid grid-cols-2 gap-4">
              <div class="space-y-1.5">
                <label class="text-sm font-medium">Client ID <span class="text-red-500">*</span></label>
                <input type="text" formControlName="clientId"
                  class="h-9 w-full rounded-md border border-border-strong bg-background px-3 text-sm" placeholder="OAuth client ID">
              </div>
              <div class="space-y-1.5">
                <label class="text-sm font-medium">Client Secret <span class="text-red-500">*</span></label>
                <input type="password" formControlName="clientSecret"
                  class="h-9 w-full rounded-md border border-border-strong bg-background px-3 text-sm" placeholder="OAuth client secret">
              </div>
              @if (selectedCredentialDirection === 'OUTBOUND') {
                <div class="space-y-1.5">
                  <label class="text-sm font-medium">Token Endpoint <span class="text-red-500">*</span></label>
                  <input type="url" formControlName="tokenEndpoint"
                    class="h-9 w-full rounded-md border border-border-strong bg-background px-3 text-sm" placeholder="https://api.service.com/oauth/token">
                </div>
              }
              <div class="space-y-1.5">
                <label class="text-sm font-medium">Scope</label>
                <input type="text" formControlName="scope"
                  class="h-9 w-full rounded-md border border-border-strong bg-background px-3 text-sm" placeholder="api read write">
                <p class="text-xs text-muted-foreground">Space-separated OAuth scopes</p>
              </div>
              <div class="space-y-1.5">
                <label class="text-sm font-medium">Access Token</label>
                <input type="password" formControlName="accessToken"
                  class="h-9 w-full rounded-md border border-border-strong bg-background px-3 text-sm" placeholder="Current access token (optional)">
              </div>
              <div class="space-y-1.5">
                <label class="text-sm font-medium">Refresh Token</label>
                <input type="password" formControlName="refreshToken"
                  class="h-9 w-full rounded-md border border-border-strong bg-background px-3 text-sm" placeholder="Refresh token (optional)">
              </div>
            </div>
          }

          @if (selectedCredentialType === 'basic_auth') {
            <div class="grid grid-cols-2 gap-4">
              <div class="space-y-1.5">
                <label class="text-sm font-medium">Username <span class="text-red-500">*</span></label>
                <input type="text" formControlName="username"
                  class="h-9 w-full rounded-md border border-border-strong bg-background px-3 text-sm" placeholder="Username">
              </div>
              <div class="space-y-1.5">
                <label class="text-sm font-medium">Password <span class="text-red-500">*</span></label>
                <input type="password" formControlName="password"
                  class="h-9 w-full rounded-md border border-border-strong bg-background px-3 text-sm" placeholder="Password">
              </div>
            </div>
          }

          @if (selectedCredentialType === 'bearer_token') {
            <div class="space-y-1.5">
              <label class="text-sm font-medium">Bearer Token <span class="text-red-500">*</span></label>
              <input type="password" formControlName="bearerToken"
                class="h-9 w-full rounded-md border border-border-strong bg-background px-3 text-sm" placeholder="Bearer token">
            </div>
          }

          @if (selectedCredentialType === 'custom') {
            <div class="space-y-2">
              <div class="flex items-center justify-between">
                <label class="text-sm font-semibold">Custom Headers</label>
                <button type="button" hlmBtn size="sm" variant="outline" (click)="addCustomHeader()">
                  <ng-icon hlm name="lucidePlus" class="size-4 shrink-0" />Add Header
                </button>
              </div>
              @for (headerGroup of getCustomHeaders().controls; track $index; let i = $index) {
                <div [formGroup]="asFormGroup(headerGroup)" hlmCard class="p-2">
                  <div class="flex items-center gap-2">
                    <input type="text" formControlName="name"
                      class="h-8 w-36 rounded border border-border bg-background px-2 text-xs" placeholder="Header name">
                    <input type="text" formControlName="value"
                      class="h-8 flex-1 rounded border border-border bg-background px-2 text-xs" placeholder="Header value">
                    <button type="button" hlmBtn variant="ghost" size="sm" class="h-8 w-8 p-0 text-red-500"
                      (click)="removeCustomHeader(i)">
                      <ng-icon hlm name="lucideTrash2" class="size-3.5" />
                    </button>
                  </div>
                </div>
              }
            </div>
          }

          <!-- OUTBOUND Configuration -->
          @if (selectedCredentialDirection === 'OUTBOUND') {
            <div class="space-y-4">
              <div class="flex items-start gap-2 rounded-lg border border-ds-accent/30 bg-ds-accent/5 px-4 py-3 text-xs text-ds-accent">
                <ng-icon hlm name="lucideInfo" class="size-4 shrink-0 mt-0.5" />
                Configure credentials WE use to access the CLIENT's endpoints (repack, customs clearance).
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div class="space-y-1.5">
                  <label class="text-sm font-medium">Client Account ID <span class="text-red-500">*</span></label>
                  <input type="text" formControlName="clientAccount"
                    class="h-9 w-full rounded-md border border-border-strong bg-background px-3 text-sm"
                    placeholder="Client's account identifier">
                  <p class="text-xs text-muted-foreground">Account identifier provided by the client</p>
                </div>
                <div class="space-y-1.5">
                  <label class="text-sm font-medium">Client Secret Key <span class="text-red-500">*</span></label>
                  <input type="password" formControlName="clientSecret"
                    class="h-9 w-full rounded-md border border-border-strong bg-background px-3 text-sm"
                    placeholder="Client's secret key">
                  <p class="text-xs text-muted-foreground">Secret key provided by the client</p>
                </div>
                <div class="col-span-2 space-y-1.5">
                  <label class="text-sm font-medium">Client API Base URL <span class="text-red-500">*</span></label>
                  <input type="url" formControlName="clientBaseUrl"
                    class="h-9 w-full rounded-md border border-border-strong bg-background px-3 text-sm"
                    placeholder="https://client-api.example.com">
                  <p class="text-xs text-muted-foreground">Base URL of the client's API endpoints</p>
                </div>
                <div class="space-y-1.5">
                  <label class="text-sm font-medium">Connection Timeout (s)</label>
                  <input type="number" formControlName="connectionTimeout"
                    class="h-9 w-full rounded-md border border-border-strong bg-background px-3 text-sm"
                    placeholder="30" min="5" max="300">
                  <p class="text-xs text-muted-foreground">Default: 30 seconds</p>
                </div>
                <div class="space-y-1.5">
                  <label class="text-sm font-medium">Webhook Secret</label>
                  <input type="password" formControlName="webhookSecret"
                    class="h-9 w-full rounded-md border border-border-strong bg-background px-3 text-sm"
                    placeholder="Webhook validation secret">
                  <p class="text-xs text-muted-foreground">Secret for validating incoming webhooks</p>
                </div>
              </div>
            </div>
          }

          <!-- Expiration + Description (shared) -->
          <div class="grid grid-cols-2 gap-4">
            <div class="space-y-1.5">
              <label class="text-sm font-medium">Expiration Date</label>
              <input type="date" formControlName="expirationDate"
                class="h-9 w-full rounded-md border border-border-strong bg-background px-3 text-sm"
                [class.bg-stone-50]="isAutoPopulatedInboundCredentials()"
                [readonly]="isAutoPopulatedInboundCredentials()">
              <p class="text-xs text-muted-foreground">When these credentials expire (optional)</p>
            </div>
            <div class="space-y-1.5">
              <label class="text-sm font-medium">Description</label>
              <div class="flex items-start gap-2">
                <textarea formControlName="description" rows="2"
                  class="flex-1 rounded-md border px-3 py-2 text-sm"
                  [class]="isAutoPopulatedInboundCredentials() ? 'border-border bg-stone-50' : 'border-border-strong bg-background'"
                  [readonly]="isAutoPopulatedInboundCredentials()"
                  placeholder="Brief description"></textarea>
                @if (isAutoPopulatedInboundCredentials()) {
                  <span hlmBadge class="bg-stone-100 text-stone-600 ring-1 ring-stone-200 text-xs mt-1 shrink-0">System</span>
                }
              </div>
            </div>
          </div>

          <!-- Test connection (OUTBOUND only) -->
          @if (selectedCredentialDirection === 'OUTBOUND') {
            <div hlmCard class="border-ds-accent/30 bg-ds-accent/5 p-4 space-y-2">
              <div class="flex items-center gap-2">
                <input type="checkbox" formControlName="testConnection" id="testConnectionCheck"
                  class="h-4 w-4 rounded border-border accent-ds-accent">
                <label for="testConnectionCheck" class="text-sm">Test connection after saving credentials</label>
              </div>
              <button type="button" hlmBtn size="sm" variant="outline"
                (click)="testOutboundConnection()" [disabled]="isTesting || secretsForm.invalid">
                @if (isTesting) { <span class="me-1.5 h-3 w-3 animate-spin rounded-full border-2 border-border border-t-foreground"></span> }
                @if (!isTesting) { <ng-icon hlm name="lucideWifi" class="size-4 shrink-0" /> }Test Connection Now
              </button>
            </div>
          }

        </div>

        <!-- Footer -->
        <div class="flex items-center justify-between border-t border-border px-5 py-4">
          <div class="text-xs text-muted-foreground">
            @if (selectedCredentialDirection) {
              Direction: <strong>{{ selectedCredentialDirection }}</strong> |
              Type: <strong>{{ getCredentialAuthTypeDisplay() }}</strong> |
              Source: <strong [class]="getCredentialSourceLabel() === 'Auto-generated' ? 'text-green-700' : 'text-ds-accent'">
                {{ getCredentialSourceLabel() }}
              </strong>
            }
          </div>
          <div class="flex gap-2">
            <button type="button" hlmBtn variant="outline" (click)="cancelSecretsModal()" [disabled]="isSaving">
              Cancel
            </button>
            @if (isJwtInboundSelected && !isAutoPopulatedInboundCredentials()) {
              <button type="button" hlmBtn
                class="border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
                (click)="regenerateJwtSecret()" [disabled]="isSaving">
                @if (isSaving) { <span class="me-1.5 h-3.5 w-3.5 animate-spin rounded-full border-2 border-amber-200 border-t-amber-700"></span> }
                @if (!isSaving) { <ng-icon hlm name="lucideRefreshCw" class="size-4 shrink-0" /> }Regenerate JWT Secret
              </button>
            }
            <button type="submit" hlmBtn [disabled]="isSaving || secretsForm.invalid">
              @if (isSaving) { <span class="me-2 h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white"></span> }
              {{ isAutoPopulatedInboundCredentials() ? 'Update Auto-Generated Credentials' : 'Save Changes' }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
}

<!-- API Credentials Modal -->
@if (showCredentialsModal) {
  <div class="fixed inset-0 z-50 grid place-items-center p-4"
    (keydown.escape)="cancelCredentialsModal()"
    role="dialog" aria-labelledby="credentialsModalTitle">
    <div class="absolute inset-0 bg-stone-900/40 backdrop-blur-[2px]" (click)="cancelCredentialsModal()"></div>
    <div hlmCard class="relative z-10 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
      <div class="flex items-center justify-between border-b border-border px-5 py-4">
        <h3 id="credentialsModalTitle" class="text-base font-semibold flex items-center gap-2">
          <ng-icon hlm name="lucideShieldCheck" class="size-4 shrink-0 text-green-600" />
          API Credentials Management
          <span hlmBadge class="bg-ds-accent/10 text-ds-accent ring-1 ring-ds-accent/20 text-xs">
            {{ selectedClient?.clientName }}
          </span>
        </h3>
        <button type="button" hlmBtn variant="ghost" size="sm" class="h-7 w-7 p-0" (click)="cancelCredentialsModal()">
          <ng-icon hlm name="lucideX" class="size-4" />
        </button>
      </div>

      <div class="space-y-5 p-5">
        <div class="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          <ng-icon hlm name="lucideInfo" class="size-4 shrink-0 mt-0.5" />
          Use these credentials to authenticate when calling our API endpoints from your client application.
        </div>

        <!-- Existing Credentials -->
        @if (clientCredentials) {
          <div class="space-y-3">
            <h4 class="text-sm font-semibold flex items-center gap-2 text-green-700">
              <ng-icon hlm name="lucideCheckCircle" class="size-4 shrink-0" />Current Credentials
            </h4>
            <div hlmCard class="p-4">
              <div class="grid grid-cols-2 gap-4">
                <div class="space-y-1.5">
                  <label class="text-xs font-medium">API Account</label>
                  <div class="flex gap-1">
                    <input type="text" class="h-8 flex-1 rounded border border-border bg-stone-50 px-2 text-xs"
                      [value]="clientCredentials.apiAccount" readonly>
                    <button type="button" hlmBtn variant="outline" size="sm" class="h-8 px-2"
                      (click)="copyToClipboard(clientCredentials.apiAccount, 'API Account')">
                      <ng-icon hlm name="lucideClipboard" class="size-3.5" />
                    </button>
                  </div>
                </div>
                <div class="space-y-1.5">
                  <label class="text-xs font-medium">Status</label>
                  <div class="flex h-8 items-center">
                    <span hlmBadge [class]="clientCredentials.isActive ? 'bg-green-50 text-green-700 ring-1 ring-green-200' : 'bg-red-50 text-red-700 ring-1 ring-red-200'" class="text-xs">
                      {{ clientCredentials.isActive ? 'Active' : 'Inactive' }}
                    </span>
                  </div>
                </div>
                <div class="col-span-2 space-y-1.5">
                  <label class="text-xs font-medium">Created</label>
                  <p class="text-xs text-muted-foreground">{{ clientCredentials.createdAtUtc | date:'medium' }}</p>
                </div>
                <div class="col-span-2 space-y-1.5">
                  <label class="text-xs font-medium">Allowed Endpoints</label>
                  <div class="rounded border border-border bg-stone-50 p-2 space-y-1">
                    @for (endpoint of clientCredentials.allowedEndpoints; track endpoint) {
                      <div><code class="text-xs text-ds-accent">{{ endpoint }}</code></div>
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        }

        <!-- No Credentials -->
        @if (!clientCredentials) {
          <div class="flex flex-col items-center gap-2 py-10 text-center text-muted-foreground">
            <ng-icon hlm name="lucideShieldOff" class="size-8" />
            <p class="text-sm font-medium text-foreground">No API Credentials Generated</p>
            <p class="text-xs">Generate credentials to allow this client to access your API endpoints.</p>
          </div>
        }

        <!-- Newly generated credentials (show once) -->
        @if (showCredentialsInfo && generatedCredentials) {
          <div class="space-y-3">
            <div class="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <ng-icon hlm name="lucideAlertTriangle" class="size-4 shrink-0 mt-0.5" />
              <strong>Security Notice:</strong> These credentials will only be shown once. Copy immediately. The secret is cleared in 5 minutes.
            </div>
            <h4 class="text-sm font-semibold flex items-center gap-2 text-ds-accent">
              <ng-icon hlm name="lucideKey" class="size-4 shrink-0" />New Credentials Generated
            </h4>
            <div hlmCard class="ring-1 ring-ds-accent/40 p-4 space-y-4">
              <div class="grid grid-cols-2 gap-4">
                <div class="space-y-1.5">
                  <label class="text-xs font-medium">API Account</label>
                  <div class="flex gap-1">
                    <input type="text" class="h-8 flex-1 rounded border border-border bg-stone-50 px-2 text-xs"
                      [value]="generatedCredentials.apiAccount" readonly>
                    <button type="button" hlmBtn variant="outline" size="sm" class="h-8 px-2"
                      (click)="copyToClipboard(generatedCredentials.apiAccount, 'API Account')">
                      <ng-icon hlm name="lucideClipboard" class="size-3.5" />
                    </button>
                  </div>
                </div>
                <div class="space-y-1.5">
                  <label class="text-xs font-medium">Secret Key</label>
                  <div class="flex gap-1">
                    <input type="text" class="h-8 flex-1 rounded border border-border bg-stone-50 px-2 font-mono text-xs"
                      [value]="generatedCredentials.apiSecretKey" readonly>
                    <button type="button" hlmBtn variant="outline" size="sm" class="h-8 px-2"
                      (click)="copyToClipboard(generatedCredentials.apiSecretKey, 'Secret Key')">
                      <ng-icon hlm name="lucideClipboard" class="size-3.5" />
                    </button>
                  </div>
                  <p class="text-[11px] text-muted-foreground flex items-center gap-0.5"><ng-icon hlm name="lucideShieldAlert" class="size-3.5 shrink-0" />Cleared from clipboard in 30s</p>
                </div>
              </div>
              @if (generatedCredentials.exampleJwtHeader) {
                <div class="space-y-2">
                  <label class="text-xs font-medium">Example JWT Header</label>
                  <pre class="rounded-md bg-stone-950 px-4 py-3 font-mono text-[11px] text-stone-100 overflow-x-auto">{{'{'}}
  "alg": "{{ generatedCredentials.exampleJwtHeader!.alg }}",
  "typ": "{{ generatedCredentials.exampleJwtHeader!.typ }}",
  "account": "{{ generatedCredentials.exampleJwtHeader!.account }}"
{{'}'}}</pre>
                </div>
              }
              @if (generatedCredentials.allowedEndpoints?.length) {
                <div class="space-y-1.5">
                  <label class="text-xs font-medium">Allowed API Endpoints</label>
                  <div class="rounded border border-border bg-stone-50 p-2 space-y-1">
                    @for (ep of generatedCredentials.allowedEndpoints; track ep) {
                      <div><code class="text-xs text-green-700">{{ ep }}</code></div>
                    }
                  </div>
                </div>
              }
            </div>
          </div>
        }
      </div>

      <div class="flex justify-end gap-2 border-t border-border px-5 py-4">
        <button type="button" hlmBtn variant="outline" (click)="cancelCredentialsModal()" [disabled]="isSaving">
          Close
        </button>
        @if (clientCredentials) {
          <button type="button" hlmBtn
            class="border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
            (click)="regenerateCredentials()" [disabled]="isSaving">
            @if (isSaving) { <span class="me-1.5 h-3.5 w-3.5 animate-spin rounded-full border-2 border-amber-200 border-t-amber-700"></span> }
            @if (!isSaving) { <ng-icon hlm name="lucideRefreshCw" class="size-4 shrink-0" /> }Regenerate Credentials
          </button>
        }
        @if (!clientCredentials) {
          <button type="button" hlmBtn (click)="generateCredentials()" [disabled]="isSaving">
            @if (isSaving) { <span class="me-1.5 h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white"></span> }
            @if (!isSaving) { <ng-icon hlm name="lucideShieldPlus" class="size-4 shrink-0" /> }Generate Credentials
          </button>
        }
      </div>
    </div>
  </div>
}
